import { useRef, useCallback } from 'react';
import { Link as ReactLink, useHistory } from 'react-router-dom';

import style from './style.module.css';

const MOVE_THRESHOLD = 10;

interface ActionProps {
    action?: string | (() => void);
    target?: string;
    className?: string;
    children?: React.ReactNode;
    style?: React.CSSProperties;
}

const Action: React.FC<ActionProps> = ({ action , target = '_blank', className = '', ...props }) => {
    const dest = action;
    const actionType = dest && typeof dest;
    const history = useHistory();
    const touchStartRef = useRef<{ x: number; y: number } | null>(null);

    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        const touch = e.touches[0];
        touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    }, []);

    const handleTouchEnd = useCallback((e: React.TouchEvent) => {
        if (!touchStartRef.current) return;
        const touch = e.changedTouches[0];
        const dx = Math.abs(touch.clientX - touchStartRef.current.x);
        const dy = Math.abs(touch.clientY - touchStartRef.current.y);
        touchStartRef.current = null;
        if (dx > MOVE_THRESHOLD || dy > MOVE_THRESHOLD) return;
        e.preventDefault();
        if (typeof dest === 'function') {
            dest();
        } else if (typeof dest === 'string' && dest[0] === '/') {
            history.push(dest);
        }
    }, [dest, history]);

    if (actionType === 'function') {
        return (
            <span
                className={`${style.action} ${className}`}
                onClick={dest as () => void}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
                {...props}
            />
        );
    }

    if (actionType === 'string' && typeof dest === 'string' && dest[0] === '/') {
        return (
            <ReactLink
                className={`${style.action} ${className}`}
                to={dest}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
                {...props}
            />
        );
    }

    if (actionType === 'string' && typeof dest === 'string' && dest[0] === '#') {
        return <a className={`${style.action} ${className}`} href={dest} {...props} />;
    }

    if (actionType === 'string' && typeof dest === 'string' && dest[0] !== '/') {
        return (
            <a
                className={`${style.action} ${className}`}
                href={dest}
                rel="noopener noreferrer"
                {...(target ? { target } : {})}
                {...props}
            />
        );
    }

    return <span className={`${style.action} ${style.noaction} ${className}`} {...props} />;
};

export default Action;
